import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import config from '../config/api';
import {
  CreditCardIcon,
  FolderIcon,
  DocumentTextIcon,
  ServerIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  PlusIcon,
  EyeIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  BoltIcon,
  CpuChipIcon,
  SignalIcon,
  ChartBarIcon,
  ArrowRightIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

const Dashboard = () => {
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [wallet, setWallet] = useState(null);
  const [projects, setProjects] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(amount || 0);
  };

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const headers = { 'Authorization': `Bearer ${token}` };

        // Fetch all data in parallel
        const [walletRes, projectsRes, contractsRes] = await Promise.all([
          fetch(config.WALLET_API_URL, { headers }).catch(() => null),
          fetch(config.PROJECTS_API_URL, { headers }).catch(() => null),
          fetch(config.CONTRACTS_API_URL, { headers }).catch(() => null)
        ]);

        if (walletRes?.ok) {
          const data = await walletRes.json();
          setWallet(data);
        }
        if (projectsRes?.ok) {
          const data = await projectsRes.json();
          setProjects(Array.isArray(data) ? data : []);
        }
        if (contractsRes?.ok) {
          const data = await contractsRes.json();
          setContracts(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch wallet history
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const res = await fetch(config.WALLET_API_URL + '/history', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setTransactions(Array.isArray(data) ? data.slice(0, 5) : []);
        }
      } catch (error) {
        console.error('Error fetching history:', error);
      }
    };
    fetchHistory();
  }, []);

  const stats = [
    {
      title: 'Số dư ví',
      value: wallet ? `${formatCurrency(wallet.balance)} ₫` : '---',
      icon: <CreditCardIcon className="w-6 h-6" />,
      change: wallet?.locked_amount ? `${formatCurrency(wallet.locked_amount)} ₫ đang khóa` : null,
      href: '/wallet'
    },
    {
      title: 'Dự án',
      value: projects.length,
      icon: <FolderIcon className="w-6 h-6" />,
      change: `${projects.filter(p => p.status === 'ACTIVE' || p.status === 'IN_PROGRESS').length} đang hoạt động`,
      href: '/projects'
    },
    {
      title: 'Hợp đồng',
      value: contracts.length,
      icon: <DocumentTextIcon className="w-6 h-6" />,
      change: `${contracts.filter(c => c.status === 'ACTIVE').length} đang hiệu lực`,
      href: '/contracts'
    },
    {
      title: 'Hosting',
      value: '---',
      icon: <ServerIcon className="w-6 h-6" />,
      change: 'Đang cập nhật',
      href: '/hosting'
    }
  ];

  const quickActions = [
    { name: 'Nạp tiền', icon: <PlusIcon className="w-6 h-6" />, href: '/wallet', desc: 'Nạp tiền vào ví' },
    { name: 'Tạo ticket', icon: <SparklesIcon className="w-6 h-6" />, href: '/support', desc: 'Yêu cầu hỗ trợ' },
    { name: 'Xem dự án', icon: <EyeIcon className="w-6 h-6" />, href: '/projects', desc: 'Quản lý dự án' },
    { name: 'Hợp đồng', icon: <DocumentTextIcon className="w-6 h-6" />, href: '/contracts', desc: 'Xem hợp đồng' }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      'ACTIVE': { label: 'Hoạt động', class: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
      'IN_PROGRESS': { label: 'Đang thực hiện', class: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      'COMPLETED': { label: 'Hoàn thành', class: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
      'PENDING': { label: 'Chờ xử lý', class: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
      'CANCELLED': { label: 'Đã hủy', class: 'bg-red-500/20 text-red-400 border-red-500/30' },
      'EXPIRED': { label: 'Hết hạn', class: 'bg-slate-500/20 text-slate-400 border-slate-500/30' }
    };
    const config = statusConfig[status] || { label: status, class: 'bg-slate-500/20 text-slate-400 border-slate-500/30' };
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${config.class}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-cyan-400/70">Đang tải dữ liệu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden bg-gradient-to-r from-dark-800 via-dark-700 to-dark-800 rounded-2xl p-6 md:p-8 border border-cyan-500/10">
        {/* Background effects */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl"></div>
        </div>

        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `linear-gradient(rgba(0, 240, 255, 0.03) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(0, 240, 255, 0.03) 1px, transparent 1px)`,
          backgroundSize: '30px 30px'
        }}></div>

        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <BoltIcon className="w-5 h-5 text-cyan-400" />
              <span className="text-cyan-400/70 text-sm font-mono">DASHBOARD</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Xin chào, <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">{user?.company_name || user?.name || 'Khách hàng'}</span>!
            </h1>
            <p className="text-ocean-100/60">
              Chào mừng bạn quay trở lại. Dưới đây là tổng quan về tài khoản của bạn.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="flex items-center gap-2 text-cyan-400/70 text-sm mb-1">
                <CalendarIcon className="w-4 h-4" />
                <span>{currentTime.toLocaleDateString('vi-VN', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
              </div>
              <div className="text-2xl font-bold text-cyan-400 font-mono">
                {currentTime.toLocaleTimeString('vi-VN')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {stats.map((stat, index) => (
          <Link
            key={index}
            to={stat.href}
            className="group relative bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-cyan-500/10 hover:border-cyan-500/30 transition-all duration-300 hover:-translate-y-1"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity"></div>

            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center text-cyan-400 border border-cyan-500/20 group-hover:shadow-glow-cyan transition-shadow">
                  {stat.icon}
                </div>
                <ArrowRightIcon className="w-5 h-5 text-cyan-400/50 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
              </div>

              <div className="text-2xl md:text-3xl font-bold text-white mb-1 font-mono">
                {stat.value}
              </div>
              <div className="text-ocean-100/50 text-sm font-medium mb-2">{stat.title}</div>
              {stat.change && (
                <div className="text-xs text-cyan-400/70">{stat.change}</div>
              )}
            </div>

            {/* Bottom accent line */}
            <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </Link>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-cyan-500/10">
        <div className="flex items-center gap-2 mb-6">
          <CpuChipIcon className="w-5 h-5 text-cyan-400" />
          <h2 className="text-lg font-bold text-white">Thao tác nhanh</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <Link
              key={index}
              to={action.href}
              className="group flex flex-col items-center justify-center p-6 rounded-2xl bg-dark-700/50 border border-cyan-500/10 hover:border-cyan-500/30 hover:bg-cyan-500/5 transition-all duration-300 hover:-translate-y-1"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center text-cyan-400 mb-4 border border-cyan-500/20 group-hover:shadow-glow-cyan transition-shadow">
                {action.icon}
              </div>
              <span className="text-white font-semibold text-sm mb-1">{action.name}</span>
              <span className="text-ocean-100/50 text-xs">{action.desc}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Projects */}
        <div className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-cyan-500/10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <FolderIcon className="w-5 h-5 text-cyan-400" />
              <h2 className="text-lg font-bold text-white">Dự án gần đây</h2>
            </div>
            <Link to="/projects" className="text-cyan-400 text-sm hover:text-cyan-300 transition-colors flex items-center gap-1">
              Xem tất cả <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>

          {projects.length > 0 ? (
            <div className="space-y-4">
              {projects.slice(0, 5).map((project) => (
                <div key={project.id} className="flex items-center justify-between p-4 bg-dark-700/50 rounded-xl border border-cyan-500/5 hover:border-cyan-500/20 transition-colors">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">{project.name}</h3>
                    <p className="text-ocean-100/50 text-sm truncate">{project.description || 'Không có mô tả'}</p>
                  </div>
                  <div className="ml-4">
                    {getStatusBadge(project.status)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FolderIcon className="w-12 h-12 text-ocean-100/20 mx-auto mb-3" />
              <p className="text-ocean-100/50">Chưa có dự án nào</p>
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-cyan-500/10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <ChartBarIcon className="w-5 h-5 text-cyan-400" />
              <h2 className="text-lg font-bold text-white">Giao dịch gần đây</h2>
            </div>
            <Link to="/wallet" className="text-cyan-400 text-sm hover:text-cyan-300 transition-colors flex items-center gap-1">
              Xem tất cả <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>

          {transactions.length > 0 ? (
            <div className="space-y-4">
              {transactions.map((tx, index) => (
                <div key={tx.id || index} className="flex items-center justify-between p-4 bg-dark-700/50 rounded-xl border border-cyan-500/5">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      tx.type === 'DEPOSIT' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {tx.type === 'DEPOSIT' ? <ArrowTrendingUpIcon className="w-5 h-5" /> : <ArrowTrendingDownIcon className="w-5 h-5" />}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">
                        {tx.description || (tx.type === 'DEPOSIT' ? 'Nạp tiền' : 'Rút tiền')}
                      </p>
                      <p className="text-ocean-100/50 text-xs">
                        {dayjs.utc(tx.created_at).tz('Asia/Ho_Chi_Minh').format('DD/MM/YYYY HH:mm')}
                      </p>
                    </div>
                  </div>
                  <div className={`text-right font-bold font-mono ${
                    tx.type === 'DEPOSIT' ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {tx.type === 'DEPOSIT' ? '+' : '-'}{formatCurrency(Math.abs(tx.amount))} ₫
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <ChartBarIcon className="w-12 h-12 text-ocean-100/20 mx-auto mb-3" />
              <p className="text-ocean-100/50">Chưa có giao dịch nào</p>
            </div>
          )}
        </div>
      </div>

      {/* System Status */}
      <div className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-cyan-500/10">
        <div className="flex items-center gap-2 mb-6">
          <SignalIcon className="w-5 h-5 text-cyan-400" />
          <h2 className="text-lg font-bold text-white">Trạng thái hệ thống</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { name: 'API Server', status: 'online' },
            { name: 'Database', status: 'online' },
            { name: 'CDN', status: 'online' },
            { name: 'Email Service', status: 'online' }
          ].map((service, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-dark-700/50 rounded-xl border border-cyan-500/5">
              <span className="text-ocean-100/70 text-sm">{service.name}</span>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-glow-cyan"></span>
                <span className="text-emerald-400 text-xs font-medium">Online</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
